import React, { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useBilling } from '../hooks/useBilling';
import { BRAND } from '../brand';
import { smoothScrollToElement } from '../utils/smoothScroll';

/* ---------------- icons ---------------- */

const HexagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 5h6M14 5h6M4 9h16M4 13h6M14 13h6M4 17h16M4 21h6M14 21h6" />
  </svg>
);

const LuoshuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
    <path d="M9.2 3.5v17M14.8 3.5v17M3.5 9.2h17M3.5 14.8h17" />
  </svg>
);

const PillarsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M5 3.5v17M9.7 3.5v17M14.3 3.5v17M19 3.5v17" />
    <path d="M3.5 7.5h17" opacity=".45" />
  </svg>
);

const ScrollIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3.5h11.5A1.5 1.5 0 0 1 19 5v14a1.5 1.5 0 0 1-1.5 1.5H6" />
    <path d="M6 3.5A2.5 2.5 0 0 0 3.5 6v12A2.5 2.5 0 0 0 6 20.5" />
    <path d="M8.5 8h6M8.5 12h6M8.5 16h3.5" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20.2a7.5 7.5 0 0 1 15 0" />
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2.5 5.5 13.2H11L10 21.5l7.5-10.7H12l1-8.3Z" />
  </svg>
);

const navItems = [
  { to: '/app/meihua', label: '梅花', title: '梅花易数', icon: <HexagramIcon /> },
  { to: '/app/qimen', label: '奇门', title: '奇门遁甲', icon: <LuoshuIcon /> },
  { to: '/app/bazi', label: '八字', title: '八字命理', icon: <PillarsIcon /> },
  { to: '/app/history', label: '记录', title: '求测记录', icon: <ScrollIcon /> },
  { to: '/app/settings', label: '我的', title: '我的', icon: <UserIcon /> },
];

/** 三个排盘页的主锚点：切 tab 时落到盘面，而不是页头或页尾 AI。 */
const CHART_ANCHOR: Record<string, string> = {
  '/app/meihua': 'meihua-result',
  '/app/qimen': 'qimen-chart',
  '/app/bazi': 'bazi-chart',
};

const scrollToAnchor = (id: string): boolean => {
  const el = document.getElementById(id);
  if (!el) return false;
  smoothScrollToElement(el);
  return true;
};

const isItemActive = (pathname: string, to: string) => {
  if (to === '/app/settings') {
    return pathname.startsWith('/app/settings') || pathname.startsWith('/app/pricing');
  }
  return pathname.startsWith(to);
};

export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance, planName, loading: billingLoading } = useBilling();

  const activeItem = navItems.find((item) => isItemActive(location.pathname, item.to));
  const activeTitle = location.pathname.startsWith('/app/pricing')
    ? '订阅计划'
    : activeItem?.title ?? '观象';
  const wide = location.pathname.startsWith('/app/qimen') || location.pathname.startsWith('/app/bazi');

  const initial = user?.displayName?.charAt(0) || user?.email?.charAt(0) || BRAND.seal;
  const userName = user?.displayName || user?.email?.split('@')[0] || '观象用户';
  const creditLabel = billingLoading && balance === null ? '—' : String(balance ?? '—');

  useEffect(() => {
    const target = CHART_ANCHOR[location.pathname];
    if (!target) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    // 直接从当前滚动位置滑到盘面；页面淡入会遮住内容切换的第一帧，
    // 不再先跳回页首，所以不会出现“先闪一下顶部再滑下去”。
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      if (scrollToAnchor(target)) return;
      if (attempts++ < 20) {
        window.setTimeout(tick, 80);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    const timer = window.setTimeout(tick, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  return (
    <div className="app-frame">
      <aside className="app-sidebar">
        <button className="app-sidebar-brand" onClick={() => navigate('/app/meihua')} aria-label={`${BRAND.name}首页`}>
          <span className="app-seal">{BRAND.seal}</span>
          <span className="app-brand-text">
            <span className="app-wordmark">{BRAND.name}</span>
            <span className="app-brand-en">{BRAND.english}</span>
          </span>
        </button>

        <nav className="app-sidebar-nav" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => {
                if (location.pathname === item.to) {
                  const anchor = CHART_ANCHOR[item.to];
                  if (anchor) scrollToAnchor(anchor);
                }
              }}
              className={`app-sidebar-link ${isItemActive(location.pathname, item.to) ? 'is-active' : ''}`}
            >
              <span className="app-sidebar-icon">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="app-sidebar-foot">
          <button className="sidebar-credit" onClick={() => navigate('/app/pricing')}>
            <span className="sidebar-credit-head">
              <BoltIcon />
              AI 解读
            </span>
            <span className="sidebar-credit-value">
              <b>{creditLabel}</b> 次
            </span>
            <span className="sidebar-credit-plan">{planName}</span>
          </button>

          <button className="sidebar-user" onClick={() => navigate('/app/settings')}>
            <span className="app-avatar">{initial}</span>
            <span className="sidebar-user-text">
              <span className="sidebar-user-name">{userName}</span>
              <span className="sidebar-user-sub">{planName}</span>
            </span>
          </button>
        </div>
      </aside>

      <div className="app-body">
        <header className="app-topbar">
          <button className="app-topbar-brand" onClick={() => navigate('/app/meihua')} aria-label={`${BRAND.name}首页`}>
            <span className="app-seal app-seal-sm">{BRAND.seal}</span>
            <span className="app-wordmark">{BRAND.name}</span>
          </button>
          <div className="app-topbar-title">{activeTitle}</div>
          <div className="app-topbar-actions">
            <button className="credit-chip" onClick={() => navigate('/app/pricing')} title="查看额度与订阅">
              <BoltIcon />
              <span>{creditLabel}</span>
            </button>
            <button className="app-avatar app-avatar-sm" onClick={() => navigate('/app/settings')} aria-label="我的账户">
              {initial}
            </button>
          </div>
        </header>

        <main key={location.pathname} className={`app-main ${wide ? 'is-wide' : ''} page-enter`}>
          <Outlet />
        </main>
      </div>

      <nav className="app-dock" aria-label="主导航">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => {
              if (location.pathname === item.to) {
                const anchor = CHART_ANCHOR[item.to];
                if (anchor) scrollToAnchor(anchor);
              }
            }}
            className={`app-dock-item ${isItemActive(location.pathname, item.to) ? 'is-active' : ''}`}
          >
            <span className="app-dock-icon">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
